import { useMemo, useEffect } from 'react';

import { useQuery } from '@zenbusiness/application-commons-graphql';

import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import { useLogError } from '#client/hooks/useLogError';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccount from '#client/hooks/useUnitAccount';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { GET_BANK_CUSTOMER_ADDRESS } from '#client/queries';

export default function useAccountDetails() {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { unitAccount } = useUnitAccount();
  const { createCommonErrorToast } = useToastContext();
  const { unitAccountId, isEnabled, isEnrolled, isValidMfa } = useUnitAccountId();
  const { error, data, errors } = useQuery(GET_BANK_CUSTOMER_ADDRESS, {
    variables: {
      businessEntityUuid
    },
    skip: !isEnabled || !isEnrolled || !isValidMfa || !unitAccountId,
    noThrow: true
  });

  useLogError(errors, error);
  useEffect(() => {
    if (error) {
      createCommonErrorToast({ error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  // convert unitAccount?.address attributes from null or undefined to empty string
  const bankCustomerAddress = useMemo(() => {
    const { address1, address2, city, state, zip } = data?.bankCustomerAddress ?? {};
    return {
      address1: address1 ?? '',
      address2: address2 ?? '',
      city: city ?? '',
      state: state ?? '',
      zip: zip ?? ''
    };
  }, [data]);

  const { address1, address2, city, state, zip } = bankCustomerAddress;

  const copyAddress = useMemo(() => {
    if (data?.bankCustomerAddress) {
      return `${address1} ${address2 ? `${address2} ` : ''}${city}, ${state}, ${zip}`;
    }
    return '';
  }, [address1, address2, city, data?.bankCustomerAddress, zip, state]);

  const renderAddress = useMemo(() => {
    if (data?.bankCustomerAddress) {
      return [`${address1} ${address2 ? `${address2} ` : ''}`, `${city}, ${state}, ${zip}`];
    }
    return ['', ''];
  }, [address1, address2, city, data?.bankCustomerAddress, zip, state]);

  const { accountNumber, routingNumber, name } = unitAccount ?? {};
  const accountNumberText = accountNumber ?? '**********';
  const routingNumberText = routingNumber ?? '**********';
  const nameText = name ?? '';

  return {
    accountNumberText,
    bankCustomerAddress,
    copyAddress,
    nameText,
    renderAddress,
    routingNumberText
  };
}
